import { Box, Paper, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { GoPlus } from "react-icons/go";
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { ActionButtons } from '../../../Components/Form/ActionButtons/action-buttons.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { DestroyBoxesUseCase } from '../../../Usecase/Boxes/destroy-boxes-usecase';
import { ListBoxesUseCase } from '../../../Usecase/Boxes/list-boxes-usecase';
import { useListBoxes } from './box-list.hook';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Can } from '../../../Components/Can';

interface IBoxList {
  listBoxesUseCase: ListBoxesUseCase
  destroyBoxesUseCase: DestroyBoxesUseCase
}

const BoxList : FC<IBoxList> = ({ listBoxesUseCase, destroyBoxesUseCase }) => {
    const navigate = useNavigate()

    const {
      listBoxes,
      list,
      setBoxToDestroy,
      boxToDestroy,
      boxToDestroyName,
      handleDestroyBox,
      totalRegisters,
      loading,
      loadError,
      page,
      handlePageChange,
    } = useListBoxes({
      listBoxesUseCase,
      destroyBoxesUseCase,
    })

    useEffect(() => {
        listBoxes()
    }, [listBoxes])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'code',
            headerName: 'Código',
            minWidth: 140,
            flex: 1,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={`/box-form/${params.row.id}`}
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {params.value || '—'}
              </Box>
            ),
          },
          {
            field: 'length',
            headerName: 'Comprimento',
            width: 120,
            sortable: false,
            renderCell: (params: any) => (
              <Typography variant="body2" color="text.secondary">
                {params.value != null ? `${params.value} cm` : '—'}
              </Typography>
            ),
          },
          {
            field: 'width',
            headerName: 'Largura',
            width: 110,
            sortable: false,
            renderCell: (params: any) => (
              <Typography variant="body2" color="text.secondary">
                {params.value != null ? `${params.value} cm` : '—'}
              </Typography>
            ),
          },
          {
            field: 'height',
            headerName: 'Altura',
            width: 110,
            sortable: false,
            renderCell: (params: any) => (
              <Typography variant="body2" color="text.secondary">
                {params.value != null ? `${params.value} cm` : '—'}
              </Typography>
            ),
          },
          {
            field: "actions",
            headerName: "Ações",
            width: 80,
            sortable: false,
            renderCell: (params: any) => (
              <ActionButtons
                params={params}
                setItemToDestroy={setBoxToDestroy}
                editLink={`/box-form/${params.row.id}`}
              />
            ),
          },
        ];

        return createColumns;
      }, [setBoxToDestroy]);

    return (
      <Can permissions={['menu:boxes:manage']}>
        <BaseScreen>
        <ConfirmDialog
          title='Remover Caixa'
          onCancel={() => setBoxToDestroy('')}
          description={`Tem certeza que deseja remover "${boxToDestroyName}"?`}
          open={!!boxToDestroy}
          onConfirm={handleDestroyBox}
        />
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage
                    title='Caixas'
                    subtitle='Gerencie as caixas de embalagem e suas dimensões'
                    breadcrumb={[{ title: 'Caixas', link: "/box-list"}, { title: 'Listagem'}]}
                    minHeight="auto"
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/box-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        totalCount={totalRegisters}
                        columns={columns}
                        page={page}
                        onPageChange={handlePageChange}
                        rowHeight={52}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={52} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar as caixas."
                      onRetry={() => listBoxes()}
                    />
                  ) : (
                    <Placeholder description="Nenhuma caixa encontrada." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
      </Can>
    )
}

export { BoxList };
